import { editorUrl } from '@/lib/editorBasePath';
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  YearbookPage,
  YearbookElement,
  PageBackground,
  ToolType,
  DEFAULT_TEXT_STYLE,
  PAGE_WIDTH,
  PAGE_HEIGHT,
  Position,
} from '@/types/yearbook';
import { LayoutTemplateDef } from '@/lib/parseTemplateObjects';
import { parseTemplateObjects, ApiTemplateObject } from '@/lib/parseTemplateObjects';
import { useHistory } from './useHistory';
import { useAutoSave } from './useAutoSave';

const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Returns true if the element should survive a template/layout change.
 * Primary signal: explicit `userAdded === true`.
 * Legacy fallback (for elements saved before `userAdded` existed): keep when
 * `userAdded` is undefined AND not `fromLayout` AND not a typical template-only type.
 */
const TEMPLATE_ONLY_TYPES = new Set(['slot', 'free_slot', 'shape', 'shape_border']);
function isUserAddedElement(el: YearbookElement): boolean {
  if (el.userAdded === true) return true;
  if (el.userAdded === undefined && !el.fromLayout && !TEMPLATE_ONLY_TYPES.has(el.type)) return true;
  return false;
}

export function useYearbook(
  initialPages: YearbookPage[],
  preloadedIndices: Set<number>,
  defaultFont: string = 'DM Sans',
  lockedPageNumbers?: Set<number>
) {
  const {
    pages,
    setPages,
    loadPages,
    mergePages,
    beginDrag,
    dragUpdate,
    endDrag,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory(initialPages);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // Multi-selection state
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [activeTool, setActiveTool] = useState<ToolType>('inspiration');

  // Compat: single selection derived from multi-selection
  const selectedElementId = selectedElementIds.length > 0 ? selectedElementIds[selectedElementIds.length - 1] : null;
  const setSelectedElementId = useCallback((id: string | null) => {
    setSelectedElementIds(id ? [id] : []);
  }, []);

  const { loadedIndices, syncSnapshot, injectGuideInSnapshot, flushSave, loadAllPages, ensureIndicesLoaded, isSaving, allowEmptySaveFor } = useAutoSave(pages, mergePages, currentPageIndex, preloadedIndices, lockedPageNumbers);

  const currentPage = pages[currentPageIndex];

  const selectedElement =
    currentPage?.elements.find((el) => el.id === selectedElementId) || null;

  const selectedElements =
    currentPage?.elements.filter((el) => selectedElementIds.includes(el.id)) || [];

  // Refs to access latest values in keyboard handler without re-registering
  const pagesRef = useRef(pages);
  const currentPageIndexRef = useRef(currentPageIndex);
  const selectedElementIdsRef = useRef(selectedElementIds);
  const clipboardRef = useRef<YearbookElement[]>([]);
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  useEffect(() => { currentPageIndexRef.current = currentPageIndex; }, [currentPageIndex]);
  useEffect(() => { selectedElementIdsRef.current = selectedElementIds; }, [selectedElementIds]);

  // Reference comparison is sufficient since useHistory uses structuredClone
  const findChangedPage = useCallback((prev: YearbookPage[], next: YearbookPage[]) => {
    for (let i = 0; i < Math.max(prev.length, next.length); i++) {
      if (prev[i] !== next[i] && JSON.stringify(prev[i]) !== JSON.stringify(next[i])) return i;
    }
    return -1;
  }, []);

  const onRestoreNav = useCallback((prev: YearbookPage[], next: YearbookPage[]) => {
    const idx = findChangedPage(prev, next);
    if (idx !== -1) setCurrentPageIndex(idx);
  }, [findChangedPage, setCurrentPageIndex]);

  const undoWithNav = useCallback(() => undo(onRestoreNav), [undo, onRestoreNav]);
  const redoWithNav = useCallback(() => redo(onRestoreNav), [redo, onRestoreNav]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditable =
        target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT';

      const selIds = selectedElementIdsRef.current;

      // Copy element(s) (Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selIds.length > 0) {
        const currentPageNow = pagesRef.current[currentPageIndexRef.current];
        const els = currentPageNow?.elements.filter(
          (el) => selIds.includes(el.id) && el.type !== 'slot'
        );
        if (els && els.length > 0) {
          clipboardRef.current = structuredClone(els);
        }
      }

      // Paste element(s) (Ctrl+V) — only when NOT in an editable field
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isEditable && clipboardRef.current.length > 0) {
        e.preventDefault();
        const offset = 15;
        const newEls = clipboardRef.current.map((source) => ({
          ...structuredClone(source),
          id: generateId(),
          position: {
            x: Math.min(source.position.x + offset, PAGE_WIDTH - source.size.width - 10),
            y: Math.min(source.position.y + offset, PAGE_HEIGHT - source.size.height - 10),
          },
        }));
        setPages((prev) =>
          prev.map((page, idx) => {
            if (idx !== currentPageIndexRef.current) return page;
            return { ...page, elements: [...page.elements, ...newEls] };
          })
        );
        setSelectedElementIds(newEls.map((el) => el.id));
        clipboardRef.current = newEls.map((el) => ({ ...el }));
      }

      // Ignore remaining shortcuts when typing in editable fields
      if (isEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoWithNav();
      }
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault();
        redoWithNav();
      }
      // Delete selected element(s)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selIds.length > 0) {
        e.preventDefault();
        const currentPageNow = pagesRef.current[currentPageIndexRef.current];

        // Separate slots-with-images (clear image) from other elements (remove)
        const slotsToClear: string[] = [];
        const toRemove: string[] = [];

        for (const id of selIds) {
          const el = currentPageNow?.elements.find((e) => e.id === id);
          if (!el) continue;
          if (el.type === 'slot' && !el.imageUrl) continue; // empty slot — skip
          if (el.type === 'slot' && el.imageUrl) {
            slotsToClear.push(id);
          } else {
            toRemove.push(id);
          }
        }

        if (slotsToClear.length === 0 && toRemove.length === 0) return;

        setPages((prev) =>
          prev.map((page, idx) => {
            if (idx !== currentPageIndexRef.current) return page;
            let elements = page.elements;
            if (slotsToClear.length > 0) {
              elements = elements.map((el) =>
                slotsToClear.includes(el.id)
                  ? { ...el, imageUrl: undefined, imageOffset: undefined, imageScale: undefined, pictureId: undefined }
                  : el
              );
            }
            if (toRemove.length > 0) {
              elements = elements.filter((el) => !toRemove.includes(el.id));
            }
            // Keyboard deletion has its own state-update path. Explicitly allow
            // saving an empty page here too, otherwise autosave's safety guard
            // rejects deletion of the last free text/image element.
            if (elements.length === 0 && page.elements.length > 0) {
              allowEmptySaveFor([page.id]);
            }
            return { ...page, elements };
          })
        );
        setSelectedElementIds([]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undoWithNav, redoWithNav, setPages, allowEmptySaveFor]);

  const updatePageCommit = useCallback(
    (pageIndex: number, updater: (page: YearbookPage) => YearbookPage) => {
      setPages((prev) =>
        prev.map((p, i) => (i === pageIndex ? updater(p) : p))
      );
    },
    [setPages]
  );

  const updatePageDrag = useCallback(
    (pageIndex: number, updater: (page: YearbookPage) => YearbookPage) => {
      dragUpdate((prev) =>
        prev.map((p, i) => (i === pageIndex ? updater(p) : p))
      );
    },
    [dragUpdate]
  );

  const isTranche = useCallback(
    (idx: number) => pagesRef.current[idx]?.coverType === 'tranche',
    []
  );

  const addStickerElement = useCallback(
    (
      imageUrl: string,
      naturalWidth: number,
      naturalHeight: number,
      opts?: { format?: 'png' | 'svg' | 'simple_svg'; defaultColor?: string }
    ) => {
      if (isTranche(currentPageIndex)) return;
      // Use natural size, clamped to page usable area
      const PAGE_MARGIN = 56.6929; // 20mm in points
      const maxW = PAGE_WIDTH / 4;
      const maxH = PAGE_HEIGHT - 2 * PAGE_MARGIN;
      const scaleToFit = Math.min(1, maxW / naturalWidth, maxH / naturalHeight);
      const w = Math.round(naturalWidth * scaleToFit);
      const h = Math.round(naturalHeight * scaleToFit);
      const fmt = opts?.format;
      const isRecolorableSvg = fmt === 'svg';
      const isSimpleSvg = fmt === 'simple_svg';
      const newElement: YearbookElement = {
        id: generateId(),
        type: 'image',
        position: { x: PAGE_WIDTH / 2 - w / 2, y: PAGE_HEIGHT / 2 - h / 2 },
        size: { width: w, height: h },
        rotation: 0,
        imageUrl,
        imageRatio: naturalWidth / naturalHeight,
        isSticker: true,
        userAdded: true,
        ...(isRecolorableSvg ? { stickerFormat: 'svg' as const, stickerColor: opts?.defaultColor || '#000000' } : {}),
        ...(isSimpleSvg ? { stickerFormat: 'simple_svg' as const } : {}),
      };
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, newElement],
      }));
      setSelectedElementId(newElement.id);
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const addQrCodeElement = useCallback(
    (imageUrl: string, naturalWidth: number, naturalHeight: number, qrcodeId: number) => {
      if (isTranche(currentPageIndex)) return;
      const maxW = 150;
      const scaleQr = Math.min(1, maxW / naturalWidth);
      const w = Math.round(naturalWidth * scaleQr);
      const h = Math.round(naturalHeight * scaleQr);
      const newElement: YearbookElement = {
        id: generateId(),
        type: 'image',
        position: { x: PAGE_WIDTH / 2 - w / 2, y: PAGE_HEIGHT / 2 - h / 2 },
        size: { width: w, height: h },
        rotation: 0,
        imageUrl,
        imageRatio: naturalWidth / naturalHeight,
        qrcodeId,
        userAdded: true,
      };
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, newElement],
      }));
      setSelectedElementId(newElement.id);
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const addShapeElement = useCallback(
    (shapeType: 'rect' | 'circle' | 'line', color: string = '#000000') => {
      if (isTranche(currentPageIndex)) return;
      const sizes: Record<string, { w: number; h: number }> = {
        rect: { w: 120, h: 80 },
        circle: { w: 100, h: 100 },
        line: { w: 200, h: 4 },
      };
      const { w, h } = sizes[shapeType];
      const newElement: YearbookElement = {
        id: generateId(),
        type: 'shape',
        position: { x: PAGE_WIDTH / 2 - w / 2, y: PAGE_HEIGHT / 2 - h / 2 },
        size: { width: w, height: h },
        rotation: 0,
        shapeType,
        textStyle: { fontSize: 0, fontFamily: '', fontWeight: '', color, textAlign: 'left', italic: false, underline: false },
        userAdded: true,
      };
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, newElement],
      }));
      setSelectedElementId(newElement.id);
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const addElements = useCallback(
    (newElements: YearbookElement[]) => {
      if (isTranche(currentPageIndex)) return;
      if (!newElements || newElements.length === 0) return;
      const tagged = newElements.map((e) => (e.userAdded ? e : { ...e, userAdded: true }));
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, ...tagged],
      }));
      setSelectedElementIds(tagged.map((el) => el.id));
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const addTextElement = useCallback((variant?: 'title' | 'subtitle' | 'text', customContent?: string) => {
    const variantStyles: Record<string, { fontSize: number; fontWeight: string; italic: boolean; content: string; y: number; height: number; textAlign?: 'left' | 'center' | 'right' }> = {
      title: { fontSize: 32, fontWeight: '700', italic: false, content: 'Titre', y: 40, height: 50, textAlign: 'center' },
      subtitle: { fontSize: 18, fontWeight: '400', italic: true, content: 'Sous-titre', y: 100, height: 35, textAlign: 'center' },
      text: { fontSize: 16, fontWeight: '400', italic: false, content: 'Double-cliquez pour éditer', y: PAGE_HEIGHT / 2 - 20, height: 30 },
    };
    const v = variantStyles[variant ?? 'text'];
    const newElement: YearbookElement = {
      id: generateId(),
      type: 'text',
      position: { x: PAGE_WIDTH / 2 - 100, y: v.y },
      size: { width: 200, height: v.height },
      rotation: 0,
      content: customContent ?? v.content,
      textStyle: { ...DEFAULT_TEXT_STYLE, fontFamily: defaultFont, fontSize: v.fontSize, fontWeight: v.fontWeight, italic: v.italic, ...(v.textAlign ? { textAlign: v.textAlign } : {}) },
      userAdded: true,
    };
    updatePageCommit(currentPageIndex, (page) => ({
      ...page,
      elements: [...page.elements, newElement],
    }));
    setSelectedElementId(newElement.id);
  }, [currentPageIndex, updatePageCommit]);

  const addImageElement = useCallback(
    (imageUrl: string, imageRatio?: number, pictureId?: number, thumbUrl?: string) => {
      if (isTranche(currentPageIndex)) return;
      // Priority 1a: selected free_slot → convert to image
      const targetFreeSlot = selectedElementId
        ? currentPage?.elements.find(
            (el) => el.id === selectedElementId && el.type === 'free_slot'
          )
        : null;
      if (targetFreeSlot) {
        updatePageCommit(currentPageIndex, (page) => ({
          ...page,
          elements: page.elements.map((el) =>
            el.id === targetFreeSlot.id
              ? {
                  ...el,
                  type: 'image' as const,
                  imageUrl,
                  imageRatio,
                  pictureId,
                  thumbnail: thumbUrl,
                  imageOffset: { x: 50, y: 50 },
                  previewImageUrl: undefined,
                }
              : el
          ),
        }));
        setSelectedElementId(targetFreeSlot.id);
        return;
      }

      // Priority 1: selected slot
      const targetSlot = selectedElementId
        ? currentPage?.elements.find(
            (el) => el.id === selectedElementId && el.type === 'slot'
          )
        : null;
      // Priority 2: first empty slot
      const emptySlot =
        targetSlot ||
        currentPage?.elements.find((el) => el.type === 'slot' && !el.imageUrl);

      if (emptySlot) {
        updatePageCommit(currentPageIndex, (page) => ({
          ...page,
          elements: page.elements.map((el) =>
            el.id === emptySlot.id
              ? { ...el, imageUrl, imageRatio, imageOffset: undefined, pictureId, thumbnail: thumbUrl }
              : el
          ),
        }));
        // Only update selection if user explicitly selected this slot (targetSlot),
        // otherwise deselect to avoid overwriting the same slot on next photo click.
        if (targetSlot) {
          setSelectedElementId(emptySlot.id);
        } else {
          setSelectedElementId(null);
        }
        return;
      }

      // Use natural ratio, clamped so the image fits within 1/3 page width and page height
      const maxW = PAGE_WIDTH / 3;
      const maxH = PAGE_HEIGHT / 3;
      let w: number, h: number;
      if (imageRatio) {
        // Start with max width, compute height from ratio
        w = maxW;
        h = Math.round(w / imageRatio);
        // If height exceeds max, scale down
        if (h > maxH) {
          h = maxH;
          w = Math.round(h * imageRatio);
        }
      } else {
        w = Math.round(maxW);
        h = 150;
      }
      const newElement: YearbookElement = {
        id: generateId(),
        type: 'image',
        position: { x: PAGE_WIDTH / 2 - w / 2, y: PAGE_HEIGHT / 2 - h / 2 },
        size: { width: w, height: h },
        rotation: 0,
        imageUrl,
        imageRatio,
        pictureId,
        thumbnail: thumbUrl,
        userAdded: true,
      };
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, newElement],
      }));
      setSelectedElementId(newElement.id);
      setActiveTool('photos');
    },
    [currentPage, currentPageIndex, selectedElementId, updatePageCommit, isTranche]
  );

  const updateElement = useCallback(
    (elementId: string, updates: Partial<YearbookElement>) => {
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: page.elements.map((el) =>
          el.id === elementId ? { ...el, ...updates } : el
        ),
      }));
    },
    [currentPageIndex, updatePageCommit]
  );

  /** Live drag update – no undo push */
  const updateElementDrag = useCallback(
    (elementId: string, updates: Partial<YearbookElement>) => {
      updatePageDrag(currentPageIndex, (page) => ({
        ...page,
        elements: page.elements.map((el) =>
          el.id === elementId ? { ...el, ...updates } : el
        ),
      }));
    },
    [currentPageIndex, updatePageDrag]
  );

  /** Batch drag update for multiple elements – no undo push */
  const updateMultipleElementsDrag = useCallback(
    (updates: Array<{ id: string; position: Position }>) => {
      updatePageDrag(currentPageIndex, (page) => ({
        ...page,
        elements: page.elements.map((el) => {
          const u = updates.find((u) => u.id === el.id);
          return u ? { ...el, position: u.position } : el;
        }),
      }));
    },
    [currentPageIndex, updatePageDrag]
  );

  const deleteElement = useCallback(
    (elementId: string) => {
      updatePageCommit(currentPageIndex, (page) => {
        const target = page.elements.find((el) => el.id === elementId);
        const groupId = target?.groupId;
        const toRemove = new Set<string>([elementId]);
        if (groupId) {
          for (const el of page.elements) {
            if (el.groupId === groupId) toRemove.add(el.id);
          }
        }
        const remaining = page.elements.filter((el) => !toRemove.has(el.id));
        // Deleting the last element(s) of a page is a legitimate user action:
        // whitelist the page so the "never save an empty page" guard doesn't
        // silently block the autosave (the element would reappear on reload).
        if (remaining.length === 0) allowEmptySaveFor([page.id]);
        return { ...page, elements: remaining };
      });
      setSelectedElementIds((prev) => {
        const page = pagesRef.current[currentPageIndex];
        const target = page?.elements.find((el) => el.id === elementId);
        const groupId = target?.groupId;
        if (groupId) {
          const groupIds = new Set(page!.elements.filter((e) => e.groupId === groupId).map((e) => e.id));
          return prev.filter((id) => !groupIds.has(id));
        }
        return prev.filter((id) => id !== elementId);
      });
    },
    [currentPageIndex, updatePageCommit, allowEmptySaveFor]
  );

  const deleteMultipleElements = useCallback(
    (elementIds: string[]) => {
      updatePageCommit(currentPageIndex, (page) => {
        const remaining = page.elements.filter((el) => !elementIds.includes(el.id));
        if (remaining.length === 0) allowEmptySaveFor([page.id]);
        return { ...page, elements: remaining };
      });
      setSelectedElementIds([]);
    },
    [currentPageIndex, updatePageCommit, allowEmptySaveFor]
  );

  const setPageBackground = useCallback(
    (bgType: 'color' | 'image', value: string, backgroundTemplateId?: number, isCustom?: boolean) => {
      // Tranche page only supports its own background via the text panel — no page background changes.
      if (isTranche(currentPageIndex)) return;
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        background: {
          type: bgType,
          value,
          trame_background_id: isCustom ? null : (backgroundTemplateId ?? null),
          trame_background_custom_id: isCustom ? (backgroundTemplateId ?? null) : null,
        },
      }));
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const duplicateElement = useCallback(
    (elementId: string) => {
      const element = currentPage?.elements.find((el) => el.id === elementId);
      if (!element) return;
      const newElement: YearbookElement = {
        ...element,
        id: generateId(),
        position: {
          x: element.position.x + 20,
          y: element.position.y + 20,
        },
      };
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, newElement],
      }));
      setSelectedElementId(newElement.id);
    },
    [currentPage, currentPageIndex, updatePageCommit]
  );

  const duplicateMultipleElements = useCallback(
    (elementIds: string[]) => {
      const elements = currentPage?.elements.filter((el) => elementIds.includes(el.id));
      if (!elements || elements.length === 0) return;
      const groupIdMap = new Map<string, string>();
      const newElements = elements.map((el) => {
        const clone: YearbookElement = {
          ...structuredClone(el),
          id: generateId(),
          position: { x: el.position.x + 20, y: el.position.y + 20 },
        };
        if (el.groupId) {
          let newGid = groupIdMap.get(el.groupId);
          if (!newGid) {
            newGid = generateId();
            groupIdMap.set(el.groupId, newGid);
          }
          clone.groupId = newGid;
        }
        return clone;
      });
      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        elements: [...page.elements, ...newElements],
      }));
      setSelectedElementIds(newElements.map((e) => e.id));
    },
    [currentPage, currentPageIndex, updatePageCommit]
  );

  const moveElementLayer = useCallback(
    (elementId: string, direction: 'up' | 'down') => {
      updatePageCommit(currentPageIndex, (page) => {
        const idx = page.elements.findIndex((el) => el.id === elementId);
        if (idx === -1) return page;
        const newElements = [...page.elements];
        const target = direction === 'up' ? idx + 1 : idx - 1;
        if (target < 0 || target >= newElements.length) return page;
        [newElements[idx], newElements[target]] = [
          newElements[target],
          newElements[idx],
        ];
        return { ...page, elements: newElements };
      });
    },
    [currentPageIndex, updatePageCommit]
  );

  const applyLayout = useCallback(
    (layout: LayoutTemplateDef, templateId?: number) => {
      if (isTranche(currentPageIndex)) return;
      const existingImages: { imageUrl: string; imageRatio?: number; imageOffset?: { x: number; y: number }; pictureId?: number }[] = [];
      if (currentPage) {
        for (const el of currentPage.elements) {
          if (el.type === 'slot' && el.imageUrl) {
            existingImages.push({ imageUrl: el.imageUrl, imageRatio: el.imageRatio, imageOffset: el.imageOffset, pictureId: el.pictureId });
          }
        }
      }

      const slotElements: YearbookElement[] = layout.slots.map((slot, i) => ({
        id: generateId(),
        type: (slot.isFreeSlot ? 'free_slot' : 'slot') as 'slot' | 'free_slot',
        position: { x: slot.x, y: slot.y },
        size: { width: slot.width, height: slot.height },
        rotation: slot.rotation ?? 0,
        fromLayout: true,
        isCircle: slot.isCircle,
        backgroundColor: slot.backgroundColor,
        zPriority: slot.zPriority,
        zIndex: slot.zIndex,
        cornerRadii: slot.cornerRadii,
        // Don't reuse existing slot images for free_slots (they'd convert immediately to image)
        ...(!slot.isFreeSlot && existingImages[i] ? {
          imageUrl: existingImages[i].imageUrl,
          imageRatio: existingImages[i].imageRatio,
          imageOffset: existingImages[i].imageOffset,
          pictureId: existingImages[i].pictureId,
        } : {}),
      }));
      const textElements: YearbookElement[] = layout.texts.map((txt) => ({
        id: generateId(),
        type: 'text' as const,
        position: { x: txt.x, y: txt.y },
        size: { width: txt.width, height: txt.height },
        rotation: txt.rotation,
        content: txt.content,
        fromLayout: true,
        backgroundColor: txt.backgroundColor,
        zPriority: txt.zPriority,
        zIndex: txt.zIndex,
        cornerRadii: txt.cornerRadii,
        textStyle: {
          fontSize: txt.fontSize,
          fontFamily: txt.fontFamily,
          fontWeight: txt.fontWeight ?? '400',
          color: txt.color,
          textAlign: txt.textAlign ?? 'left',
          italic: false,
          underline: false,
        },
      }));
      const extraElements: YearbookElement[] = layout.extras.map((ext) => {
        if (ext.type === 'sticker' || ext.type === 'template_sticker') {
          return {
            id: generateId(),
            type: 'image' as const,
            position: { x: ext.x, y: ext.y },
            size: { width: ext.width, height: ext.height },
            rotation: ext.rotation,
            imageUrl: ext.imageUrl || '',
            fromLayout: true,
            locked: ext.locked,
            backgroundColor: ext.backgroundColor,
            zPriority: ext.zPriority,
            zIndex: ext.zIndex,
            isSticker: true,
            forceDimension: ext.forceDimension,
            cornerRadii: ext.cornerRadii,
          };
        }
        if (ext.type === 'shape') {
          return {
            id: generateId(),
            type: 'shape' as const,
            position: { x: ext.x, y: ext.y },
            size: { width: ext.width, height: ext.height },
            rotation: ext.rotation,
            shapeType: (ext.shapeType as 'rect' | 'line' | 'circle') || 'rect',
            fromLayout: true,
            backgroundColor: ext.backgroundColor,
            zPriority: ext.zPriority,
            zIndex: ext.zIndex,
            cornerRadii: ext.cornerRadii,
            textStyle: { fontSize: 0, fontFamily: '', fontWeight: '', color: ext.color ?? '#000000', textAlign: 'left' as const, italic: false, underline: false },
          };
        }
        // shape_border
        return {
          id: generateId(),
          type: 'shape_border' as const,
          position: { x: ext.x, y: ext.y },
          size: { width: ext.width, height: ext.height },
          rotation: ext.rotation,
          fromLayout: true,
          backgroundColor: ext.backgroundColor,
          zPriority: ext.zPriority,
          zIndex: ext.zIndex,
          cornerRadii: ext.cornerRadii,
          textStyle: { fontSize: 0, fontFamily: '', fontWeight: '', color: ext.color ?? '#000000', textAlign: 'left' as const, italic: false, underline: false },
        };
      });

      // Merge all layout elements and sort by zIndex (priority) then zPriority (fallback)
      const allLayoutElements = [...slotElements, ...textElements, ...extraElements];
      const orderP = (z?: 'front' | 'back') => (z === 'back' ? -1 : z === 'front' ? 1 : 0);
      allLayoutElements.sort((a, b) => {
        const priorityA = a.zIndex ?? orderP(a.zPriority);
        const priorityB = b.zIndex ?? orderP(b.zPriority);
        return priorityA - priorityB;
      });

      updatePageCommit(currentPageIndex, (page) => ({
        ...page,
        layoutTemplateId: templateId ?? null,
        background: layout.backgroundImageUrl
          ? { type: 'image' as const, value: layout.backgroundImageUrl, trame_background_id: layout.trameBackgroundId ?? null, trame_background_custom_id: null }
          : layout.backgroundColor
          ? { type: 'color' as const, value: layout.backgroundColor, trame_background_id: null, trame_background_custom_id: null }
          : { type: 'color' as const, value: '#ffffff', trame_background_id: null, trame_background_custom_id: null },
        elements: [
          ...page.elements.filter(isUserAddedElement),
          ...allLayoutElements,
        ],
      }));
      setSelectedElementId(null);
    },
    [currentPage, currentPageIndex, updatePageCommit, isTranche]
  );

  const applyCover = useCallback(
    (
      coverElements: {
        elements_front: ApiTemplateObject[];
        elements_back: ApiTemplateObject[];
      }
    ) => {
      const frontIdx = pages.findIndex((p) => p.coverType === 'front');
      const backIdx = pages.findIndex((p) => p.coverType === 'back');

      console.log('[applyCover] frontIdx:', frontIdx, 'backIdx:', backIdx);
      console.log('[applyCover] elements_front:', coverElements.elements_front);
      console.log('[applyCover] elements_back:', coverElements.elements_back);

      // Carry photos from the old cover slots into the new cover's empty slots
      const carryPhotos = (oldElements: YearbookElement[], newElements: YearbookElement[]): YearbookElement[] => {
        const photos = oldElements.filter(
          (el) => !isUserAddedElement(el) && el.type === 'slot' && el.imageUrl
        );
        if (photos.length === 0) return newElements;
        let i = 0;
        return newElements.map((el) => {
          if (i < photos.length && el.type === 'slot' && !el.imageUrl) {
            const p = photos[i++] as any;
            return {
              ...el,
              imageUrl: p.imageUrl,
              imageRatio: p.imageRatio,
              pictureId: p.pictureId,
              thumbnail: p.thumbnail,
              previewImageUrl: p.previewImageUrl,
              imageOffset: undefined,
              imageScale: undefined,
            } as YearbookElement;
          }
          return el;
        });
      };

      if (frontIdx !== -1) {
        const { elements, background } = parseTemplateObjects(coverElements.elements_front, generateId);
        updatePageCommit(frontIdx, (page) => ({
          ...page,
          elements: [
            ...page.elements.filter(isUserAddedElement),
            ...carryPhotos(page.elements, elements),
          ],
          background: background ?? { type: 'color' as const, value: '#ffffff', trame_background_id: null, trame_background_custom_id: null },
        }));
      }

      if (backIdx !== -1) {
        const { elements, background } = parseTemplateObjects(coverElements.elements_back, generateId);
        updatePageCommit(backIdx, (page) => ({
          ...page,
          elements: [
            ...page.elements.filter(isUserAddedElement),
            ...carryPhotos(page.elements, elements),
          ],
          background: background ?? { type: 'color' as const, value: '#ffffff', trame_background_id: null, trame_background_custom_id: null },
        }));
      }

      setSelectedElementId(null);
    },
    [pages, updatePageCommit]
  );

  const dropImage = useCallback(
    (
      imageUrl: string,
      imageRatio: number | undefined,
      position: { x: number; y: number } | null,
      slotId: string | null,
      pageIndex: number,
      pictureId?: number,
      isSticker?: boolean,
      thumbUrl?: string
    ) => {
      if (isTranche(pageIndex)) return;
      if (slotId) {
        // Replace image in slot or free image element, keeping position/size/rotation.
        // Free slots: mutate type to 'image' (definitive conversion).
        updatePageCommit(pageIndex, (page) => ({
          ...page,
          elements: page.elements.map((el) => {
            if (el.id !== slotId) return el;
            if (el.type === 'free_slot') {
              return {
                ...el,
                type: 'image' as const,
                imageUrl,
                imageRatio,
                pictureId,
                thumbnail: thumbUrl,
                imageOffset: { x: 50, y: 50 },
                imageScale: undefined,
                previewImageUrl: undefined,
              };
            }
            return { ...el, imageUrl, imageRatio, imageOffset: undefined, imageScale: undefined, pictureId, thumbnail: thumbUrl };
          }),
        }));
        setSelectedElementId(slotId);
      } else if (position) {
        let dropW: number, dropH: number;
        if (isSticker && imageRatio) {
          // Use sticker sizing logic (natural size clamped)
          const maxW = PAGE_WIDTH / 4;
          const PAGE_MARGIN = 56.6929;
          const maxH = PAGE_HEIGHT - 2 * PAGE_MARGIN;
          const naturalW = imageRatio >= 1 ? maxW : maxW * imageRatio;
          const naturalH = imageRatio >= 1 ? maxW / imageRatio : maxW;
          const scaleToFit = Math.min(1, maxW / naturalW, maxH / naturalH);
          dropW = Math.round(naturalW * scaleToFit);
          dropH = Math.round(naturalH * scaleToFit);
        } else {
          const dropMaxW = PAGE_WIDTH / 3;
          const dropMaxH = PAGE_HEIGHT / 3;
          if (imageRatio) {
            dropW = dropMaxW;
            dropH = Math.round(dropW / imageRatio);
            if (dropH > dropMaxH) {
              dropH = dropMaxH;
              dropW = Math.round(dropH * imageRatio);
            }
          } else {
            dropW = Math.round(dropMaxW);
            dropH = 150;
          }
        }
        // Center on drop position
        const finalX = Math.max(0, Math.min(PAGE_WIDTH - dropW, position.x - dropW / 2));
        const finalY = Math.max(0, Math.min(PAGE_HEIGHT - dropH, position.y - dropH / 2));
        const newElement: YearbookElement = {
          id: generateId(),
          type: 'image',
          position: { x: Math.round(finalX), y: Math.round(finalY) },
          size: { width: dropW, height: dropH },
          rotation: 0,
          imageUrl,
          imageRatio,
          pictureId,
          thumbnail: thumbUrl,
          ...(isSticker ? { isSticker: true } : {}),
        };
        updatePageCommit(pageIndex, (page) => ({
          ...page,
          elements: [...page.elements, newElement],
        }));
        setSelectedElementId(newElement.id);
      }
      if (pageIndex !== currentPageIndex) {
        setCurrentPageIndex(pageIndex);
      }
    },
    [currentPageIndex, updatePageCommit, isTranche]
  );

  const reorderPage = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      setPages((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex > fromIndex ? toIndex - 1 : toIndex, 0, moved);
        // Reassign page numbers for interior pages
        let num = 1;
        const reordered = next.map((p) => {
          if (p.coverType) return p;
          return { ...p, pageNumber: num++ };
        });

        // POST new order to backend
        const order = reordered
          .filter((p) => !p.coverType)
          .map((p) => ({ id: p.id, page_id: p.pageNumber }));

        fetch(editorUrl('/reorder_pages'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pages: order }),
        }).catch((err) => console.warn('[Reorder] Failed to save order:', err));

        // Sync autosave snapshot synchronously with the reordered pages so
        // the debounced save doesn't fire save_page for every page whose
        // pageNumber just changed.
        syncSnapshot(reordered);

        return reordered;
      });
    },
    [setPages, syncSnapshot]
  );

  const swapPages = useCallback(
    async (indexA: number, indexB: number) => {
      if (indexA === indexB) return;

      // Ensure both pages are loaded before swapping
      await ensureIndicesLoaded([indexA, indexB]);

      setPages((prev) => {
        const next = [...prev];
        [next[indexA], next[indexB]] = [next[indexB], next[indexA]];
        let num = 1;
        const reordered = next.map((p) => {
          if (p.coverType) return p;
          return { ...p, pageNumber: num++ };
        });

        const order = reordered
          .filter((p) => !p.coverType)
          .map((p) => ({ id: p.id, page_id: p.pageNumber }));

        fetch(editorUrl('/reorder_pages'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pages: order }),
        }).catch((err) => console.warn('[Swap] Failed to save order:', err));

        syncSnapshot(reordered);

        return reordered;
      });
    },
    [setPages, ensureIndicesLoaded, syncSnapshot]
  );

  const renamePage = useCallback(
    (pageId: string, name: string) => {
      setPages((prev) =>
        prev.map((p) => (p.id === pageId ? { ...p, name } : p))
      );
    },
    [setPages]
  );

  const resetPage = useCallback(
    (pageIndex: number) => {
      const target = pages[pageIndex];
      if (target) allowEmptySaveFor([target.id]);
      updatePageCommit(pageIndex, (page) => ({
        ...page,
        elements: [],
        background: { type: 'color', value: '#FFFFFF' },
        layoutTemplateId: null,
        itemType: 'Blank',
      }));
      setSelectedElementId(null);
      flushSave();
    },
    [pages, updatePageCommit, flushSave, allowEmptySaveFor]
  );

  const duplicatePageContent = useCallback(
    async (sourceIndex: number, targetIndex: number) => {
      await ensureIndicesLoaded([sourceIndex, targetIndex]);
      setPages((prev) => {
        const source = prev[sourceIndex];
        if (!source) return prev;
        // Deep-clone elements with new IDs
        const clonedElements = source.elements.map((el) => ({
          ...el,
          id: generateId(),
          // Clear pictureId to avoid conflicts
        }));
        const next = [...prev];
        next[targetIndex] = {
          ...next[targetIndex],
          elements: clonedElements,
          background: { ...source.background },
          layoutTemplateId: source.layoutTemplateId ?? null,
        };
        return next;
      });
      setCurrentPageIndex(targetIndex);
      setSelectedElementId(null);
      setTimeout(() => syncSnapshot(), 0);
    },
    [setPages, setCurrentPageIndex, ensureIndicesLoaded, syncSnapshot]
  );

  const setPageContent = useCallback(
    (pageIndex: number, elements: YearbookElement[], background?: PageBackground, itemType?: string) => {
      updatePageCommit(pageIndex, (page) => ({
        ...page,
        elements,
        ...(background ? { background } : {}),
        ...(itemType ? { itemType } : {}),
      }));
    },
    [updatePageCommit]
  );

  /** Batch-reset multiple pages in a single commit */
  const resetMultiplePages = useCallback(
    (pageIndices: number[], background: PageBackground = { type: 'color', value: '#FFFFFF' }, itemType?: string) => {
      const indexSet = new Set(pageIndices);
      const ids = pageIndices.map((i) => pages[i]?.id).filter((id): id is string => !!id);
      if (ids.length) allowEmptySaveFor(ids);
      setPages((prev) =>
        prev.map((page, i) =>
          indexSet.has(i)
            ? { ...page, elements: [], background, layoutTemplateId: null, ...(itemType ? { itemType } : {}) }
            : page
        )
      );
      flushSave();
    },
    [pages, setPages, flushSave, allowEmptySaveFor]
  );

  const addPageGuide = useCallback(
    (
      pageIndex: number,
      guide: { orientation: 'horizontal' | 'vertical'; position: number; applyToAll?: boolean }
    ) => {
      const { applyToAll, ...g } = guide;
      const id = generateId();
      if (!applyToAll) {
        updatePageCommit(pageIndex, (page) => ({
          ...page,
          guides: [...(page.guides ?? []), { id, ...g }],
        }));
        return id;
      }
      // Apply to every free (non-locked, non-cover) page currently in memory.
      // Persistence is handled server-side in a single bulk request, so we
      // inject the guide into the autosave snapshot too: the pages stay clean
      // and no save_page POST is triggered for them.
      const isEligible = (page: { coverType?: unknown; pageNumber: number }) =>
        !page.coverType && !lockedPageNumbers?.has(page.pageNumber);
      const newGuide = { id, ...g };
      const eligibleIds: string[] = [];
      setPages((prev) => {
        const next = prev.map((page) => {
          if (!isEligible(page)) return page;
          eligibleIds.push(page.id);
          const guides = page.guides ?? [];
          if (guides.some((gg) => gg.id === id)) return page;
          return { ...page, guides: [...guides, newGuide] };
        });
        return next;
      });
      injectGuideInSnapshot(newGuide, new Set(eligibleIds));
      return id;

    },
    [updatePageCommit, setPages, lockedPageNumbers, injectGuideInSnapshot]
  );


  const deletePageGuide = useCallback(
    (pageIndex: number, guideId: string) => {
      updatePageCommit(pageIndex, (page) => ({
        ...page,
        guides: (page.guides ?? []).filter((g) => g.id !== guideId),
      }));
    },
    [updatePageCommit]
  );

  /** Remove a guide from every in-memory page without triggering any save
   *  (used to roll back an "apply to all pages" that failed server-side). */
  const removeGuideEverywhere = useCallback(
    (guideId: string) => {
      setPages((prev) => {
        const next = prev.map((page) => {
          const guides = page.guides ?? [];
          if (!guides.some((g) => g.id === guideId)) return page;
          return { ...page, guides: guides.filter((g) => g.id !== guideId) };
        });
        syncSnapshot(next);
        return next;
      });
    },
    [setPages, syncSnapshot]
  );


  return {
    pages,
    currentPageIndex,
    setCurrentPageIndex,
    currentPage,
    selectedElementId,
    setSelectedElementId,
    selectedElementIds,
    setSelectedElementIds,
    selectedElement,
    selectedElements,
    activeTool,
    setActiveTool,
    addTextElement,
    addElements,
    addImageElement,
    addStickerElement,
    addQrCodeElement,
    addShapeElement,
    updateElement,
    updateElementDrag,
    updateMultipleElementsDrag,
    deleteElement,
    deleteMultipleElements,
    duplicateElement,
    duplicateMultipleElements,
    moveElementLayer,
    setPageBackground,
    applyLayout,
    applyCover,
    dropImage,
    undo: undoWithNav,
    redo: redoWithNav,
    canUndo,
    canRedo,
    beginDrag,
    endDrag,
    reorderPage,
    swapPages,
    renamePage,
    resetPage,
    duplicatePageContent,
    setPageContent,
    resetMultiplePages,
    addPageGuide,
    deletePageGuide,
    removeGuideEverywhere,
    loadedIndices,
    loadAllPages,
    ensureIndicesLoaded,
    isSaving,
  };
}